import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ChevronLeft, Briefcase, MapPin, DollarSign, Award, Users, 
  Calendar, Mail, FileText, Plus, Share2, Search, X, 
  Eye, Edit2, Trash2, ArrowUpDown, Filter, Download, UserPlus, Loader, Send,
  Globe, Phone, ExternalLink, Link2, Shield, Building2, Check, Info, Lock,
  FileCheck, HelpCircle, PhoneCall, AlertTriangle, CheckCircle, AwardIcon, Compass
} from 'lucide-react';
import { IoLogoWhatsapp } from 'react-icons/io5';
import { clientsAPI, jobsAPI, candidatesAPI, employeesAPI } from '../../../services/api';
import { toast } from 'react-toastify';
import CreateJobModal from '../../ATS/CreateJobModal';

const ClientDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('jobs'); // 'jobs' | 'candidates' | 'details'
  const [employeeNameCache, setEmployeeNameCache] = useState({});
  const [jobsSearchQuery, setJobsSearchQuery] = useState('');
  const [candidatesSearchQuery, setCandidatesSearchQuery] = useState('');
  const [showCreateJobModal, setShowCreateJobModal] = useState(false);

  const BRAND = "#1C4E6D";
  const BRAND_DARK = "#143a52";

  useEffect(() => {
    fetchClientDetails();
    fetchClientJobs();
    fetchClientCandidates();
  }, [id]);

  const fetchClientDetails = async () => {
    try {
      const response = await clientsAPI.getClientById(id);
      setClient(response.data);
      if (response.data?.contactOwner) {
        fetchEmployeeName(response.data.contactOwner);
      }
    } catch (error) {
      toast.error('Failed to fetch client details');
      navigate('/clients');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployeeName = async (empId) => {
    if (employeeNameCache[empId]) return;
    try {
      const res = await employeesAPI.getEmployeeById(empId);
      const emp = res.data;
      const name = emp.name || `${emp.firstName || ''} ${emp.lastName || ''}`.trim() || emp.email || empId;
      setEmployeeNameCache(prev => ({ ...prev, [empId]: name }));
    } catch {
      setEmployeeNameCache(prev => ({ ...prev, [empId]: empId }));
    }
  };

  const fetchClientJobs = async () => {
    setJobsLoading(true);
    try {
      const response = await jobsAPI.getJobsByClientId(id);
      setJobs(Array.isArray(response.data) ? response.data : response.data.content || []);
    } catch (error) {
      console.error('Failed to fetch client jobs:', error);
    } finally {
      setJobsLoading(false);
    }
  };

  const fetchClientCandidates = async () => {
    setCandidatesLoading(true);
    try {
      const response = await candidatesAPI.getApplicationsByClientId(id);
      setCandidates(Array.isArray(response.data) ? response.data : response.data.content || []);
    } catch (error) {
      console.error('Failed to fetch client candidates:', error);
    } finally {
      setCandidatesLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      'NEW': 'bg-blue-50 text-blue-700 border-blue-100',
      'CONTACTED': 'bg-purple-50 text-purple-700 border-purple-100',
      'QUALIFIED': 'bg-green-50 text-green-700 border-green-100',
      'LOST': 'bg-red-50 text-red-700 border-red-100',
      'WON': 'bg-emerald-50 text-emerald-700 border-emerald-100',
      'OPEN': 'bg-green-50 text-green-700 border-green-100',
      'CLOSED': 'bg-red-50 text-red-700 border-red-100',
      'ON_HOLD': 'bg-yellow-50 text-yellow-700 border-yellow-100',
      'DRAFT': 'bg-gray-50 text-gray-700 border-gray-100'
    };
    return colors[status?.toUpperCase()] || 'bg-gray-50 text-gray-700 border-gray-100';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'HIGH': 'bg-red-50 text-red-700 border-red-100',
      'MEDIUM': 'bg-amber-50 text-amber-700 border-amber-100',
      'LOW': 'bg-green-50 text-green-700 border-green-100'
    };
    return colors[priority?.toUpperCase()] || 'bg-gray-50 text-gray-700 border-gray-100';
  };

  const addressStr = (addr) => {
    if (!addr) return null;
    return [addr.street, addr.city, addr.state, addr.country, addr.zipCode]
      .filter(Boolean)
      .join(", ");
  };

  const filteredJobs = jobs.filter(j => {
    const query = jobsSearchQuery.toLowerCase();
    return (
      j.jobTitle?.toLowerCase().includes(query) ||
      j.jobCode?.toLowerCase().includes(query) ||
      j.workLocation?.toLowerCase().includes(query)
    );
  });

  const filteredCandidates = candidates.filter(c => {
    const query = candidatesSearchQuery.toLowerCase();
    return (
      c.firstName?.toLowerCase().includes(query) ||
      c.lastName?.toLowerCase().includes(query) ||
      c.primaryEmail?.toLowerCase().includes(query) ||
      c.primaryPhone?.toLowerCase().includes(query) ||
      c.jobTitle?.toLowerCase().includes(query)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-gray-500">Loading client details…</p>
        </div>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="p-6 text-center text-red-600 font-medium">
        Client not found.
      </div>
    );
  }

  const initials = (client.companyName || '?').slice(0, 2).toUpperCase();

  const FieldBox = ({ label, value, isLink = false, isEmail = false }) => {
    const display = value || null;
    return (
      <div className="flex flex-col bg-gray-50 hover:bg-gray-100/80 p-3.5 rounded-xl border border-gray-100 transition duration-150">
        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">{label}</span>
        {display ? (
          isLink ? (
            <a 
              href={display.startsWith('http') ? display : `https://${display}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline text-xs font-semibold break-all flex items-center gap-1 w-fit"
            >
              {display} <ExternalLink size={12} className="inline-block flex-shrink-0" />
            </a>
          ) : isEmail ? (
            <a 
              href={`mailto:${display}`}
              className="text-blue-600 hover:underline text-xs font-semibold break-all"
            >
              {display}
            </a>
          ) : (
            <span className="text-gray-800 text-xs font-bold break-all">{display}</span>
          )
        ) : (
          <span className="text-gray-400 italic text-xs font-normal">—</span>
        )}
      </div>
    );
  };

  const PreferenceBox = ({ label, value }) => (
    <div className="flex items-center justify-between bg-gray-50 p-3.5 rounded-xl border border-gray-100 transition hover:bg-gray-100/80">
      <span className="text-xs font-bold text-gray-500">{label}</span>
      <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${value ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-green-50 text-green-700 border border-green-200'}`}>
        {value ? "Do Not Send" : "Allow"}
      </span>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#f8fafc] flex flex-col font-sans">
      {/* ── Top Navigation Bar ── */}
      <div className="bg-white border-b border-gray-200 px-4 sm:px-6 py-2.5 sm:py-3 flex items-center justify-between sticky top-0 z-30 shadow-sm flex-shrink-0">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          <button 
            onClick={() => navigate('/clients')}
            className="flex items-center gap-1 text-xs sm:text-sm text-gray-500 hover:text-gray-800 transition-colors font-medium"
          >
            <ChevronLeft size={16} /> <span className="hidden xs:inline">Clients</span>
          </button>
          <span className="text-gray-300 text-xs sm:text-sm">/</span>
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-xs sm:text-sm font-bold text-gray-800 truncate max-w-[120px] sm:max-w-none">{client.companyName}</span>
          </div>
        </div>

        {/* Top Right "Create Job" Button */}
        <div>
          <button
            onClick={() => setShowCreateJobModal(true)}
            className="flex items-center gap-1 sm:gap-1.5 px-3 sm:px-4 py-1.5 sm:py-2 text-[10px] sm:text-xs font-bold text-white rounded-xl shadow-sm hover:opacity-90 transition"
            style={{ backgroundColor: BRAND }}
          >
            <Plus size={12} className="sm:w-3.5 sm:h-3.5" /> Create Job
          </button>
        </div>
      </div>

      {/* ── Page Body ── */}
      <div className="flex-grow max-w-[1440px] w-full mx-auto p-3 sm:p-6 space-y-6">

        {/* ── HERO BANNER CARD ── */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4 sm:p-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="flex items-center gap-3 sm:gap-4 min-w-0">
            {client.clientCompanyLogoUrl ? (
              <img
                src={client.clientCompanyLogoUrl}
                alt={client.companyName}
                className="w-12 h-12 sm:w-16 sm:h-16 rounded-xl sm:rounded-2xl object-contain border border-gray-100 shadow-sm flex-shrink-0"
              />
            ) : (
              <div
                className="w-12 h-12 sm:w-16 sm:h-16 rounded-xl sm:rounded-2xl flex items-center justify-center text-white text-lg sm:text-2xl font-bold shadow-md flex-shrink-0"
                style={{ backgroundColor: BRAND }}
              >
                {initials}
              </div>
            )}
            <div className="min-w-0">
              <h1 className="font-extrabold text-gray-900 text-lg sm:text-xl tracking-tight leading-tight truncate">{client.companyName}</h1>
              <p className="text-[10px] sm:text-xs text-gray-500 font-semibold uppercase tracking-wider mt-1">{client.industry || 'No Industry Specified'}</p>

              <div className="flex flex-wrap gap-1 mt-2">
                {client.relationshipType && (
                  <span className="px-1.5 py-0.5 text-[9px] sm:text-[10px] font-extrabold rounded-md bg-indigo-50 text-indigo-700 border border-indigo-100 uppercase tracking-wide">
                    {client.relationshipType.replace(/_/g, ' ')}
                  </span>
                )}
                {client.leadStatus && (
                  <span className={`px-1.5 py-0.5 text-[9px] sm:text-[10px] font-extrabold rounded-md border uppercase tracking-wide ${getStatusColor(client.leadStatus)}`}>
                    {client.leadStatus}
                  </span>
                )}
                {client.companyStage && (
                  <span className="px-1.5 py-0.5 text-[9px] sm:text-[10px] font-extrabold rounded-md bg-slate-50 text-slate-600 border border-slate-200 uppercase tracking-wide">
                    {client.companyStage.replace(/_/g, ' ')}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Quick Contact Links */}
          <div className="flex flex-wrap items-center gap-2 self-stretch md:self-auto justify-start md:justify-end border-t md:border-t-0 pt-4 md:pt-0 border-gray-100">
            {client.websiteUrl && (
              <a
                href={client.websiteUrl.startsWith('http') ? client.websiteUrl : `https://${client.websiteUrl}`}
                target="_blank"
                rel="noopener noreferrer"
                className="px-2.5 py-1.5 sm:px-3.5 sm:py-2 border border-gray-200 rounded-xl text-[10px] sm:text-xs font-semibold text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition flex items-center gap-1.5 shadow-sm"
              >
                <Globe size={13} className="text-gray-400" /> Website
              </a>
            )}
            {client.linkedInUrl && (
              <a
                href={client.linkedInUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="px-2.5 py-1.5 sm:px-3.5 sm:py-2 border border-gray-200 rounded-xl text-[10px] sm:text-xs font-semibold text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition flex items-center gap-1.5 shadow-sm"
              >
                <Link2 size={13} className="text-gray-400" /> LinkedIn
              </a>
            )}
            {client.email && (
              <a
                href={`mailto:${client.email}`}
                className="px-2.5 py-1.5 sm:px-3.5 sm:py-2 border border-gray-200 rounded-xl text-[10px] sm:text-xs font-semibold text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition flex items-center gap-1.5 shadow-sm"
              >
                <Mail size={13} className="text-gray-400" /> Email Client
              </a>
            )}
          </div>
        </div>

        {/* ── GRID LAYOUT ── */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">

          {/* Left Column: Compact metadata summaries */}
          <div className="lg:col-span-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-1 gap-6 items-start">

            {/* Meta stats card */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4 sm:p-6 space-y-4">
              <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest border-b pb-2 mb-2">Assignment & Activity</h3>

              <div className="space-y-3.5">
                <div className="flex items-center justify-between text-xs">
                  <span className="font-semibold text-gray-400 uppercase tracking-wider">Contact Owner</span>
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-5 rounded-full bg-gradient-to-br from-blue-400 to-indigo-400 flex items-center justify-center text-white text-[9px] font-bold">
                      {(employeeNameCache[client.contactOwner] || client.contactOwner || 'U')[0].toUpperCase()}
                    </div>
                    <span className="font-bold text-gray-800">{employeeNameCache[client.contactOwner] || client.contactOwner || 'Unassigned'}</span>
                  </div>
                </div>

                <div className="flex items-center justify-between text-xs">
                  <span className="font-semibold text-gray-400 uppercase tracking-wider">Lead Source</span>
                  <span className="font-bold text-gray-700">{client.leadSource || 'Website'}</span>
                </div>

                <div className="flex items-center justify-between text-xs">
                  <span className="font-semibold text-gray-400 uppercase tracking-wider">Created At</span>
                  <span className="font-bold text-gray-700 flex items-center gap-1"><Calendar size={13} className="text-gray-400" /> {formatDate(client.createdAt)}</span>
                </div>

                <div className="flex items-center justify-between text-xs">
                  <span className="font-semibold text-gray-400 uppercase tracking-wider">Last Activity</span>
                  <span className="font-bold text-gray-700 flex items-center gap-1"><Calendar size={13} className="text-gray-400" /> {formatDate(client.lastActivity)}</span>
                </div>
              </div>
            </div>

            {/* Address Card */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4 sm:p-6 space-y-4">
              <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest border-b pb-2 mb-2">Addresses</h3>

              <div className="space-y-4">
                {client.billingAddress && (
                  <div className="flex items-start gap-2.5">
                    <MapPin size={16} className="text-gray-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Billing Address</span>
                      <p className="text-xs text-gray-700 leading-relaxed font-semibold mt-0.5">{addressStr(client.billingAddress)}</p>
                    </div>
                  </div>
                )}

                {client.shippingAddress && !client.sameAsBilling && (
                  <div className="flex items-start gap-2.5 pt-3 border-t border-gray-50">
                    <MapPin size={16} className="text-gray-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Shipping Address</span>
                      <p className="text-xs text-gray-700 leading-relaxed font-semibold mt-0.5">{addressStr(client.shippingAddress)}</p>
                    </div>
                  </div>
                )}

                {client.sameAsBilling && (
                  <p className="text-[11px] text-gray-400 italic bg-gray-50 p-2.5 rounded-lg text-center border border-dashed">
                    Shipping same as billing
                  </p>
                )}
              </div>
            </div>

            {/* Documents section */}
            {(client.primaryAgreementUrl || client.workOrderUrl || client.supportingDocumentUrls?.length > 0) && (
              <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4 sm:p-6 space-y-4 md:col-span-2 lg:col-span-1">
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest border-b pb-2 mb-2">Client Documents</h3>
                <div className="flex flex-col gap-2">
                  {client.primaryAgreementUrl && (
                    <a
                      href={client.primaryAgreementUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-between p-3 bg-indigo-50/50 hover:bg-indigo-50 border border-indigo-100 rounded-xl text-xs font-bold text-indigo-700 transition"
                    >
                      <span className="flex items-center gap-1.5"><FileCheck size={14} /> Primary Agreement</span>
                      <Download size={14} />
                    </a>
                  )}
                  {client.workOrderUrl && (
                    <a
                      href={client.workOrderUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-between p-3 bg-indigo-50/50 hover:bg-indigo-50 border border-indigo-100 rounded-xl text-xs font-bold text-indigo-700 transition"
                    >
                      <span className="flex items-center gap-1.5"><FileCheck size={14} /> Work Order</span>
                      <Download size={14} />
                    </a>
                  )}
                  {client.supportingDocumentUrls?.map((url, i) => (
                    <a
                      key={i}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-between p-3 bg-indigo-50/50 hover:bg-indigo-50 border border-indigo-100 rounded-xl text-xs font-bold text-indigo-700 transition"
                    >
                      <span className="flex items-center gap-1.5"><FileCheck size={14} /> Supporting Doc {i + 1}</span>
                      <Download size={14} />
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right Column: Tabbed Content Container */}
          <div className="lg:col-span-8 space-y-6">

            {/* Sleek Horizontal Tab Navigation */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="flex border-b border-gray-200 bg-white px-4 sm:px-6 overflow-x-auto scrollbar-none">
                <button
                  onClick={() => setActiveTab('jobs')}
                  className={`py-4 px-3 sm:px-4 text-[10px] sm:text-xs font-bold uppercase tracking-wider border-b-2 transition-all whitespace-nowrap flex items-center gap-1.5 ${activeTab === 'jobs' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-400 hover:text-gray-700'}`}
                >
                  <Briefcase size={13} /> Associated Jobs ({jobs.length})
                </button>
                <button
                  onClick={() => setActiveTab('candidates')}
                  className={`py-4 px-3 sm:px-4 text-[10px] sm:text-xs font-bold uppercase tracking-wider border-b-2 transition-all whitespace-nowrap flex items-center gap-1.5 ${activeTab === 'candidates' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-400 hover:text-gray-700'}`}
                >
                  <Users size={13} /> Candidate Pipeline ({candidates.length})
                </button>
                <button
                  onClick={() => setActiveTab('details')}
                  className={`py-4 px-3 sm:px-4 text-[10px] sm:text-xs font-bold uppercase tracking-wider border-b-2 transition-all whitespace-nowrap flex items-center gap-1.5 ${activeTab === 'details' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-400 hover:text-gray-700'}`}
                >
                  <Info size={13} /> Full Profile Details
                </button>
              </div>

              {/* Active Tab View */}
              <div className="p-4 sm:p-6">

                {/* ── TAB 1: JOBS ── */}
                {activeTab === 'jobs' && (
                  <div className="space-y-4">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 pb-4 border-b border-gray-100">
                      <div>
                        <h3 className="font-bold text-gray-800 text-sm">Active Job Openings</h3>
                        <p className="text-[11px] text-gray-400 mt-0.5">Positions currently managed for {client.companyName}</p>
                      </div>
                      <div className="relative w-full sm:w-64">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
                        <input
                          type="text"
                          placeholder="Search jobs..."
                          value={jobsSearchQuery}
                          onChange={(e) => setJobsSearchQuery(e.target.value)}
                          className="pl-9 pr-4 py-2 text-xs border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 w-full bg-slate-50 focus:bg-white"
                        />
                      </div>
                    </div>

                    {jobsLoading ? (
                      <div className="flex items-center justify-center py-12">
                        <Loader size={20} className="animate-spin text-[#1C4E6D]" />
                      </div>
                    ) : filteredJobs.length === 0 ? (
                      <div className="text-center py-12 text-gray-400">
                        <Briefcase size={32} className="mx-auto text-gray-200 mb-2" />
                        <p className="font-bold text-xs">No matching jobs found</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 gap-3.5">
                        {filteredJobs.map(job => (
                          <div
                            key={job.id}
                            className="p-4 border border-gray-200 rounded-2xl hover:border-blue-400 hover:shadow-sm transition-all flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
                          >
                            <div className="flex items-start gap-3 min-w-0">
                              <div
                                className="w-9 h-9 rounded-xl flex items-center justify-center text-white text-xs font-extrabold flex-shrink-0"
                                style={{ backgroundColor: BRAND }}
                              >
                                {job.jobTitle?.charAt(0).toUpperCase() || '?'}
                              </div>
                              <div className="min-w-0">
                                <button
                                  onClick={() => navigate(`/ats/jobs/${job.id}`)}
                                  className="font-extrabold text-gray-900 text-sm hover:underline hover:text-blue-700 text-left block leading-snug"
                                >
                                  {job.jobTitle}
                                </button>
                                <div className="flex items-center gap-2.5 text-[10px] text-gray-400 font-semibold mt-1 flex-wrap">
                                  <span className="text-gray-500">{job.jobCode}</span>
                                  <span>•</span>
                                  <span className="flex items-center gap-0.5"><MapPin size={11} /> {job.workLocation} ({job.workModel})</span>
                                  <span>•</span>
                                  <span className="flex items-center gap-0.5"><Users size={11} /> {job.numberOfOpenings || 0} Openings</span>
                                </div>
                              </div>
                            </div>

                            <div className="flex items-center gap-2 flex-shrink-0 self-end sm:self-auto">
                              <span className={`px-2 py-0.5 text-[9px] font-extrabold rounded-md border uppercase tracking-wider ${getStatusColor(job.jobStatus)}`}>
                                {job.jobStatus || 'OPEN'}
                              </span>
                              <span className={`px-2 py-0.5 text-[9px] font-extrabold rounded-md border uppercase tracking-wider ${getPriorityColor(job.jobPriority)}`}>
                                {job.jobPriority || 'MEDIUM'}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* ── TAB 2: CANDIDATES ── */}
                {activeTab === 'candidates' && (
                  <div className="space-y-4">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 pb-4 border-b border-gray-100">
                      <div>
                        <h3 className="font-bold text-gray-800 text-sm">Submitted Candidates</h3>
                        <p className="text-[11px] text-gray-400 mt-0.5">Candidates submitted for positions under {client.companyName}</p>
                      </div>
                      <div className="relative w-full sm:w-64">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
                        <input
                          type="text"
                          placeholder="Search candidates..."
                          value={candidatesSearchQuery}
                          onChange={(e) => setCandidatesSearchQuery(e.target.value)}
                          className="pl-9 pr-4 py-2 text-xs border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 w-full bg-slate-50 focus:bg-white"
                        />
                      </div>
                    </div>

                    {candidatesLoading ? (
                      <div className="flex items-center justify-center py-12">
                        <Loader size={20} className="animate-spin text-[#1C4E6D]" />
                      </div>
                    ) : filteredCandidates.length === 0 ? (
                      <div className="text-center py-12 text-gray-400">
                        <Users size={32} className="mx-auto text-gray-200 mb-2" />
                        <p className="font-bold text-xs">No submissions matching search</p>
                      </div>
                    ) : (
                      <div>
                        {/* Mobile/Tablet Card Grid View */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 lg:hidden">
                          {filteredCandidates.map(cand => (
                            <div 
                              key={cand.id} 
                              className="p-4 bg-white border border-gray-200 rounded-2xl hover:border-blue-400 hover:shadow-sm transition-all flex flex-col justify-between gap-3"
                            >
                              <div className="flex items-center gap-3">
                                <div className="w-9 h-9 rounded-xl bg-gray-100 border flex items-center justify-center font-bold text-gray-500 text-xs flex-shrink-0">
                                  {cand.firstName?.[0]}{cand.lastName?.[0]}
                                </div>
                                <div className="min-w-0">
                                  <button
                                    onClick={() => navigate(`/candidates/${cand.id}`)}
                                    className="font-bold text-gray-900 hover:text-blue-700 hover:underline text-left block text-sm leading-snug truncate"
                                  >
                                    {cand.firstName} {cand.lastName}
                                  </button>
                                  <span className="text-[10px] text-gray-400 font-bold uppercase block mt-0.5">{cand.jobCode || 'GENERAL'}</span>
                                </div>
                              </div>

                              <div className="border-t border-gray-50 pt-2.5 space-y-1.5 text-xs text-gray-600">
                                <div className="font-semibold text-gray-800 leading-normal truncate">{cand.jobTitle || 'General Application'}</div>
                                <div className="truncate text-gray-500">{cand.primaryEmail}</div>
                                {cand.primaryPhone && <div className="text-gray-400 font-medium">{cand.primaryPhone}</div>}
                              </div>

                              <div className="flex items-center justify-between border-t border-gray-50 pt-2.5">
                                <span className="text-[10px] text-gray-400 uppercase font-bold">Status</span>
                                <span className="px-2 py-0.5 rounded text-[9px] font-extrabold bg-blue-50 text-blue-700 border border-blue-100 uppercase tracking-tight">
                                  {cand.status || 'APPLIED'}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Laptop/Desktop Table View */}
                        <div className="hidden lg:block overflow-x-auto -mx-6">
                          <table className="w-full text-left text-xs border-collapse">
                            <thead>
                              <tr className="bg-gray-50/70 border-y border-gray-100 text-gray-400 font-extrabold uppercase tracking-widest text-[9px]">
                                <th className="p-3.5 pl-6">Candidate</th>
                                <th className="p-3.5">Position Info</th>
                                <th className="p-3.5">Contacts</th>
                                <th className="p-3.5 pr-6 text-right">Status</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              {filteredCandidates.map(cand => (
                                <tr key={cand.id} className="hover:bg-slate-50/50 transition">
                                  <td className="p-3.5 pl-6">
                                    <div className="flex items-center gap-2.5">
                                      <div className="w-7 h-7 rounded-lg bg-gray-100 border flex items-center justify-center font-bold text-gray-500 text-[10px]">
                                        {cand.firstName?.[0]}{cand.lastName?.[0]}
                                      </div>
                                      <button
                                        onClick={() => navigate(`/candidates/${cand.id}`)}
                                        className="font-bold text-gray-900 hover:text-blue-700 hover:underline text-left block"
                                      >
                                        {cand.firstName} {cand.lastName}
                                      </button>
                                    </div>
                                  </td>
                                  <td className="p-3.5">
                                    <div className="font-semibold text-gray-800 leading-normal">{cand.jobTitle || 'General Application'}</div>
                                    {cand.jobCode && <div className="text-[10px] text-gray-400 font-bold uppercase mt-0.5">{cand.jobCode}</div>}
                                  </td>
                                  <td className="p-3.5">
                                    <div className="text-gray-700">{cand.primaryEmail}</div>
                                    {cand.primaryPhone && <div className="text-gray-400 font-medium mt-0.5">{cand.primaryPhone}</div>}
                                  </td>
                                  <td className="p-3.5 pr-6 text-right">
                                    <span className="px-2 py-0.5 rounded text-[9px] font-extrabold bg-blue-50 text-blue-700 border border-blue-100 uppercase tracking-tight">
                                      {cand.status || 'APPLIED'}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* ── TAB 3: DETAILS (COMPREHENSIVE) ── */}
                {activeTab === 'details' && (
                  <div className="space-y-6 animate-fade-in">

                    {/* Section 1: Company Profile details */}
                    <div>
                      <h4 className="text-xs font-bold text-indigo-600 uppercase tracking-widest mb-3 flex items-center gap-1.5">
                        <Building2 size={13} /> Company Profiler
                      </h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                        <FieldBox label="Legal Entity Name" value={client.companyName} />
                        <FieldBox label="Industry Vertical" value={client.industry} />
                        <FieldBox label="Corporate Stage" value={client.companyStage?.replace(/_/g, ' ')} />
                        <FieldBox label="Legal Form" value={client.legalType?.replace(/_/g, ' ')} />
                        <FieldBox label="Total Employee Bracket" value={client.employeeCount?.replace('RANGE_', '').replace('_', '–')} />
                        <FieldBox label="Annual Revenue Class" value={client.annualRevenue?.replace('RANGE_', '$').replace('_', '–')} />
                        <FieldBox label="Client Role / Profile" value={client.role} />
                      </div>
                    </div>

                    {/* Section 2: Contact Details */}
                    <div className="pt-5 border-t border-gray-100">
                      <h4 className="text-xs font-bold text-indigo-600 uppercase tracking-widest mb-3 flex items-center gap-1.5">
                        <Users size={13} /> Contact Information
                      </h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                        <FieldBox label="Primary Contact Name" value={client.primaryContact} />
                        <FieldBox label="Contact Job Title" value={client.jobTitle} />
                        <FieldBox label="Contact Email Address" value={client.email} isEmail />
                        <FieldBox label="Contact Phone Number" value={client.phone} />
                      </div>
                    </div>

                    {/* Section 3: Point of Contact / Hiring */}
                    <div className="pt-5 border-t border-gray-100">
                      <h4 className="text-xs font-bold text-indigo-600 uppercase tracking-widest mb-3 flex items-center gap-1.5">
                        <Compass size={13} /> Point of Contact & Hiring Management
                      </h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                        <FieldBox label="Assigned Hiring Manager" value={client.hiringManager} />
                        <FieldBox label="hiring POC Name" value={client.pocName} />
                        <FieldBox label="hiring POC Job Title" value={client.pocJobTitle} />
                        <FieldBox label="hiring POC Email" value={client.pocEmail} isEmail />
                        <FieldBox label="hiring POC Phone" value={client.pocPhone} />
                      </div>
                    </div>

                    {/* Section 4: Billing Tax Financials */}
                    <div className="pt-5 border-t border-gray-100">
                      <h4 className="text-xs font-bold text-indigo-600 uppercase tracking-widest mb-3 flex items-center gap-1.5">
                        <Shield size={13} /> Financial & Tax Attributes
                      </h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5">
                        <FieldBox label="Tax ID Type" value={client.taxIdType} />
                        <FieldBox label="Tax ID Number" value={client.taxIdNumber} />
                        <FieldBox label="Tax Status" value={client.taxStatus} />
                        <FieldBox label="Payment Terms" value={client.paymentTerms?.replace(/_/g, ' ')} />
                        <FieldBox label="Settlement Currency" value={client.currency} />
                      </div>
                    </div>

                    {/* Section 5: Preference & Rules */}
                    <div className="pt-5 border-t border-gray-100">
                      <h4 className="text-xs font-bold text-indigo-600 uppercase tracking-widest mb-3 flex items-center gap-1.5">
                        <Lock size={13} /> Privacy & Communication Restrictions
                      </h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                        <PreferenceBox label="Do Not Call (DNC)" value={client.doNotCall} />
                        <PreferenceBox label="Do Not Email (DNE)" value={client.doNotEmail} />
                      </div>
                    </div>

                  </div>
                )}

              </div>
            </div>

          </div>

        </div>

      {showCreateJobModal && (
        <CreateJobModal
          initialClientId={client.id}
          onClose={() => setShowCreateJobModal(false)}
          onCreate={() => {
            fetchClientJobs(); // Refresh jobs list
          }}
        />
      )}
      </div>
    </div>
  );
};

export default ClientDetailsPage;
